/**
 * Checks if elements are visible in a specified viewport.
 * Fires a "viewenter" and "viewleave" event when an element gets in and out of view respectively.
 *
 * Implementation example:
 * ```
 * var inView = new EN.InView($('.en-in-view-element'));
 * ```
 *
 * @module InView
 * @author EN GARDE (Martin Holler, Marcel Seelig)
 * @version 2.1
 *
 * change log:
 * v2.1 bugfix to get initial scroll position if not 0
 * v2.0 rewrite to class, new offset configuration
 * v1.2 use _.defaultsDeep instead of $.extend for settings object, fixed default in-view class selector
 * v1.0 initial release
 */

var EN = EN || {};

EN.InView = (function (_, $) {
	'use strict';

	/**
	 * @constructor
	 * @param {jQuery} $elements - Elements to watch.
	 * @param {object} [options]
	 * @property {number|string|object} options.offset={ top: 0, bottom: 0 } - One or two offset points, specified in any of the following notations: 100, "100px", "50%", { top: 100, bottom: "100px" }
	 * @property {boolean|string} options.setClasses=true - If set to true, elements will receive classes when they enter/leave the viewport. If set to "once", classes are only applied once (when entering).
	 * @property {object} classNames - Collection of class names used when `setClasses` is true.
	 * @return {InView}
	 */
	function InView($elements, options) {
		if (!$elements || !$elements.length) {
			return $elements;
		}

		var that = this;

		that.options = _.defaultsDeep(options, that.defaults);
		that.$elements = $elements;
		that.offset = that.getOffset();
		that.viewData = [];

		InView.addToWatcher(that);

		// add event listeners if required
		if (that.options.setClasses) {
			$(document).on('viewenter.InView', function (e, data) {
				if (data.instance !== that || (that.options.setClasses === 'once' && $(e.target).hasClass(that.options.classNames.isInView))) {
					return;
				}

				$(e.target).addClass([
					that.options.classNames.isInView,
					data.direction === 'top' ? that.options.classNames.fromTop : that.options.classNames.fromBottom
				].join(' '));
			});

			if (that.options.setClasses !== 'once') {
				$(document).on('viewleave.InView', function (e, data) {
					if (data.instance !== that) {
						return;
					}

					$(e.target).removeClass([
						that.options.classNames.isInView,
						that.options.classNames.fromTop,
						that.options.classNames.fromBottom
					].join(' '));
				});
			}
		}

		that.update();
	}

	// defaults
	InView.prototype.defaults = {
		offset: {
			top: 0,
			bottom: 0
		},
		setClasses: true,
		classNames: {
			isInView: 'en-is-in-view',
			fromTop: 'en-in-view-from-top',
			fromBottom: 'en-in-view-from-bottom'
		}
	};

	// properties shared across instances
	InView.instances = [];
	InView.throttleRate = 300;
	InView.scrollPosition = window.pageYOffset;
	InView.lastScrollPosition = 0;

	/**
	 * Adds an instance to the global event watcher.
	 * @param {InView} instance
	 * @return {InView}
	 */
	InView.addToWatcher = function (instance) {
		if (!(instance instanceof EN.InView) || InView.instances.indexOf(instance) !== -1) {
			return instance;
		}

		var doWatch = function (e) {
			if (e.type === 'scroll') {
				InView.scrollPosition = window.pageYOffset; // > IE 8
			}

			_.each(InView.instances, function (instance) {
				if (e.type === 'resize') {
					instance.offset = instance.getOffset();
				}

				instance.update();
			});
		};

		InView.instances.push(instance);

		// if there is no watcher yet, create it
		if (InView.instances.length === 1) {
			$(window).on('scroll.InView', _.throttle(doWatch, InView.throttleRate));
			$(window).on('resize.InView', _.debounce(doWatch, InView.throttleRate));
		}

		return instance;
	};

	/**
	 * Calculates the actual offset value(s) from the `offset` option.
	 * @memberof InView
	 * @return {object}
	 */
	InView.prototype.getOffset = function () {
		var that = this;
		var offset = {};
		var getIntValue = function (value) {
			if (typeof value === 'string' && value.slice(-1) === '%') {
				return parseInt((parseFloat(value) / 100) * window.innerHeight, 10);
			}
			else {
				return parseInt(value, 10) || 0;
			}
		};

		switch (typeof that.options.offset) {
			case 'number':
			case 'string':
				offset.top = getIntValue(that.options.offset);
				break;
			case 'object':
				_.each(that.options.offset, function (value, name) {
					offset[name] = getIntValue(value);
				});
				break;
		}

		return offset;
	};

	/**
	 * Checks if a given element is in view.
	 * @memberof InView
	 * @param {jQuery} $element
	 * @return {boolean}
	 */
	InView.prototype.isInView = function ($element) {
		var that = this;
		var elementOffset = $element.offset().top;
		var elementHeight = $element.outerHeight(false);

		if (that.offset.bottom !== undefined) {
			return InView.scrollPosition + that.offset.top <= elementOffset + elementHeight && InView.scrollPosition + window.innerHeight - that.offset.bottom >= elementOffset;
		}
		else {
			return InView.scrollPosition + that.offset.top >= elementOffset && InView.scrollPosition + that.offset.top <= elementOffset + elementHeight;
		}
	};

	/**
	 * Adds an element to the list of watched elements.
	 * @memberof InView
	 * @param {jQuery} $element
	 * @return {InView}
	 */
	InView.prototype.addElements = function ($elements) {
		var that = this;

		that.$elements = that.$elements.add($elements);
		that.update();

		return that;
	};

	/**
	 * Removes an element from the list of watched elements.
	 * @memberof InView
	 * @param {jQuery} $element
	 * @return {InView}
	 */
	InView.prototype.removeElements = function ($elements) {
		var that = this;
		var elements = $elements.toArray();

		that.$elements = that.$elements.filter(function (index) {
			if (elements.indexOf(this) !== -1) {
				that.viewData.splice(index, 1);
				return false;
			}

			return true;
		});
		that.update();

		return that;
	};

	/**
	 * Checks all watched elements and fires events if they enter/leave the view.
	 * @memberof InView
	 * @fires InView.viewenter
	 * @fires InView.viewleave
	 * @return {InView}
	 */
	InView.prototype.update = function () {
		var that = this;

		that.$elements.each(function (index) {
			var $element = $(this);
			var isInView = that.isInView($element);

			if (isInView) {
				if (!that.viewData[index]) {
					/**
					 * @event InView.viewenter
					 * @type {object}
					 * @property {string} direction - The direction from which the element entered the view ("bottom" or "top").
					 * @property {InView} instance
					 */
					$element.trigger('viewenter', {
						direction: InView.scrollPosition >= InView.lastScrollPosition ? 'bottom' : 'top',
						instance: that
					});
				}
			}
			else {
				if (that.viewData[index]) {
					/**
					 * @event InView.viewleave
					 * @type {object}
					 * @property {string} direction - The direction in which the element left the view ("bottom" or "top").
					 * @property {InView} instance
					 */
					$element.trigger('viewleave', {
						direction: InView.scrollPosition >= InView.lastScrollPosition ? 'bottom' : 'top',
						instance: that
					});
				}
			}

			that.viewData[index] = isInView;
		});

		InView.lastScrollPosition = InView.scrollPosition;

		return that;
	};

	return InView;
}(lodash, jQuery));
